import InfoPopover from 'Components/InfoPopover';
import * as React from 'react';
import { calculateFBIMODScore, fbiModFields } from 'Routes/DiagnosticSurvey/utils';
import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import InputHandler from 'Components/InputHandler';
import { IFormContext, withFormContext } from 'Containers/FormContextHandler';
import { fbimod } from 'neuro-schemas';
import { LocalizerCtx } from 'Containers/Localizer';
import { referenceText } from '../../../../../config/theme/componentTheme';

const UnformattedFormRow = ({
  children,
  ...props
}: React.PropsWithChildren<Parameters<typeof FormRow>[0]>): React.JSX.Element => (
  <FormRow formatTitle={false} {...props}>
    {children}
  </FormRow>
);

const questions: Array<keyof IFBIMOD> = [
  'apathy',
  'lackOfInitiative',
  'indifference',
  'inflexibility',
  'disregardForSelf',
  'disorganization',
  'inattentiveness',
  'unawareOfSurroundings',
  'decreaseInSpeech',
  'perseverance',
  'irritability',
  'overlyJoking',
  'badJudgement',
  'hoarding',
  'unsuitableBehaviour',
  'impulsiveness',
  'restlessness',
  'aggressiveness',
  'hyperorality',
  'hypersexuality',
  'compulsiveBehaviour',
  'incontinency',
];

const FBIMODForm = ({ formData, view, fm }: IFormContext<IFBIMOD>): React.JSX.Element => {
  const viewing = !!view?.viewing;
  const { locale } = React.useContext(LocalizerCtx);

  const localizations = fbimod.getLocalizations(locale ?? 'fi');
  const localize = (id: keyof typeof localizations): string => {
    const localized = localizations[id];
    // eslint-disable-next-line no-console
    if (!localized) console.error(`fbimod: couldn't localize id ${id}`);
    return localized ?? id;
  };

  const totalScore = React.useMemo(() => calculateFBIMODScore(formData.document), [formData.document]);
  const notFilledFields = React.useMemo(
    () => fbiModFields.filter(f => !Object.keys(formData.document).includes(f)),
    [formData.document],
  );

  return (
    <React.Fragment>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          editing={!viewing}
          name="date"
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      <UnformattedFormRow title={localize('respondent')}>
        <InputHandler
          type="Radio"
          editing={!viewing}
          name="respondent"
          formData={formData}
          options={['spouse', 'child', 'other']}
          optionFormatter={o => localize(`opts.${o}`)}
        />
      </UnformattedFormRow>
      <UnformattedFormRow title={localize('respondentOther')} condition={formData.document.respondent === 'other'}>
        <InputHandler
          type="TextField"
          editing={!viewing}
          name="respondentOther"
          formData={formData}
          placeholder={'diagnosticSurvey.fbimod.respondentOtherPlaceholder'}
        />
      </UnformattedFormRow>
      <FormSection>
        {questions.map(q => (
          <UnformattedFormRow key={q} title={localize(q)}>
            <InputHandler
              type={'Radio'}
              name={q}
              formData={formData}
              options={['0', '1', '2', '3']}
              optionFormatter={o => localize(`opts.${o}`)}
              editing={!viewing}
            />
            <div style={{ marginTop: '2rem' }}>
              <InputHandler
                type={'TextArea'}
                name={q + 'Detail'}
                formData={formData}
                editing={!viewing}
                placeholder={'diagnosticSurvey.fbimod.details'}
                width={40}
                rows={3}
              />
            </div>
          </UnformattedFormRow>
        ))}
      </FormSection>
      <FormRow title="diagnosticSurvey.fbimod.score">
        <div style={{ fontWeight: 600 }}>
          {totalScore || totalScore === 0 ? (
            fm('diagnosticSurvey.fbimod.scoreTotal', { value: totalScore })
          ) : (
            <InfoPopover
              text={
                <div style={{ whiteSpace: 'pre-line' }}>
                  <div style={{ fontWeight: 600 }}>{`${fm('diagnosticSurvey.fbimod.notFilled')} ${fm(
                    'diagnosticSurvey.fbimod.remaining',
                  )}`}</div>
                  {notFilledFields.map(field => `${localize(field)}\n`).join('')}
                </div>
              }
              color="primary"
            />
          )}
        </div>
      </FormRow>
      <div style={referenceText}>
        <p>{localize('reference')}</p>
        <p>{localize('reference2')}</p>
      </div>
    </React.Fragment>
  );
};

export default withFormContext(FBIMODForm);
